import { PROMPTS, WHITES, type PlayCard, type PromptCard, type UnoKind, type WhiteCard } from "./deck";
import { ROLES, roleById } from "./identities";
import type { Profile } from "./model";
import { uid } from "./id";

export type Secret = {
  roleId: string;
  clueIndex: number;
  exposed: boolean;
  clues: string[];
};

export type TablePlay = {
  id: string;
  from: string;
  card: PlayCard;
};

export type GameState = {
  phase: "lobby" | "play" | "judge" | "lol" | "over";
  round: number;
  direction: 1 | -1;
  judgeId: string;
  skipped: string[];
  need: Record<string, number>;
  prompt: PromptCard | null;
  punchline: string;
  toast: string;
  hands: Record<string, PlayCard[]>;
  table: TablePlay[];
  secrets: Record<string, Secret>;
  narration: string;
  lastWinner?: string;
  winPlayId: string;
  lastClue: string;
};

export function emptyGame(): GameState {
  return {
    phase: "lobby",
    round: 0,
    direction: 1,
    judgeId: "",
    skipped: [],
    need: {},
    prompt: null,
    punchline: "",
    toast: "",
    hands: {},
    table: [],
    secrets: {},
    narration: "Kick your shoes off. Family HQ is waiting.",
    lastClue: "",
    winPlayId: "",
  };
}

export function seatHouseFamily(humans: Profile[]): Profile[] {
  if (humans.length >= 4) return humans;
  const house: Profile[] = [
    {
      id: "bot-deb",
      name: "Aunt Deb",
      initial: "D",
      color: "#ff9f0a",
      creature: "snack scientist",
      vibe: "already in the group chat",
      score: 0,
      bot: true,
    },
    {
      id: "bot-jay",
      name: "Cousin Jay",
      initial: "J",
      color: "#ff375f",
      creature: "aux goblin",
      vibe: "brought juice and chaos",
      score: 0,
      bot: true,
    },
    {
      id: "bot-dog",
      name: "The Dog",
      initial: "🐕",
      color: "#ac8e68",
      creature: "honorary dog",
      vibe: "here to lol",
      score: 0,
      bot: true,
    },
  ];
  const need = Math.max(0, 4 - humans.length);
  return [...humans, ...house.slice(0, need)];
}

export function startNight(members: Profile[]): GameState {
  const seated = members.filter((m) => !m.left);
  const roles = shuffle(ROLES).slice(0, seated.length);
  const secrets: Record<string, Secret> = {};
  seated.forEach((m, i) => {
    const role = roles[i] ?? ROLES[0]!;
    secrets[m.id] = { roleId: role.id, clueIndex: 0, exposed: false, clues: [] };
  });
  const game = emptyGame();
  game.secrets = secrets;
  game.judgeId = seated.find((m) => m.bot)?.id ?? seated[0]?.id ?? "";
  game.narration = "Secret jobs are on the fridge. Don’t snitch yet. Lol first.";
  for (const m of seated) game.hands[m.id] = [];
  fillHands(game, seated.map((m) => m.id));
  beginRound(game, seated);
  return game;
}

export function beginRound(game: GameState, members: Profile[]): void {
  game.phase = "play";
  game.round += 1;
  game.table = [];
  game.skipped = [];
  game.need = {};
  game.prompt = { id: uid(), text: pick(PROMPTS) };
  const living = members.filter((m) => !m.left);
  if (!living.some((m) => m.id === game.judgeId)) game.judgeId = living[0]?.id ?? "";
  for (const m of living) {
    if (m.id === game.judgeId) continue;
    game.need[m.id] = 1;
  }
  game.toast = "";
  game.punchline = "";
  game.winPlayId = "";
  game.lastClue = "";
  fillHands(game, living.map((m) => m.id));
  const judge = nameOf(members, game.judgeId);
  if (living.some((m) => m.id !== game.judgeId && !m.bot)) {
    game.narration = `Round ${game.round} of 5 · tap your funniest card. ${judge} picks the winner.`;
  } else {
    game.narration = `Round ${game.round} of 5 · you pick the funniest. The family is filling in the blank.`;
  }
}

export function playFromHand(game: GameState, members: Profile[], from: string, cardId: string): string | null {
  if (game.phase !== "play") return "Hold up — wait for your turn.";
  if (from === game.judgeId) return "You’re picking the winner this round. Sit pretty.";
  if (game.skipped.includes(from)) return "You got skipped. Free juice. Next one.";
  const hand = game.hands[from] ?? [];
  const idx = hand.findIndex((c) => c.id === cardId);
  const card = idx === -1 ? undefined : hand[idx];
  if (!card) return "That card escaped back into the deck.";
  hand.splice(idx, 1);
  if (card.kind === "uno") {
    applyUno(game, members, from, card.uno);
    return null;
  }
  game.table.push({ id: uid(), from, card });
  const need = game.need[from] ?? 1;
  const have = game.table.filter((p) => p.from === from).length;
  if (have >= need) delete game.need[from];
  if (Object.keys(game.need).length === 0) {
    game.phase = "judge";
    game.table = shuffle(game.table);
    game.narration = `${nameOf(members, game.judgeId)} — tap the funniest card. The family is watching.`;
  }
  return null;
}

function applyUno(game: GameState, members: Profile[], from: string, uno: UnoKind): void {
  const living = members.filter((m) => !m.left);
  if (uno === "reverse") {
    game.direction = game.direction === 1 ? -1 : 1;
    game.judgeId = neighbor(living, game.judgeId, game.direction);
    rebuildNeed(game, living);
    game.toast = `REVERSE · ${nameOf(members, game.judgeId)} is picking now.`;
  } else if (uno === "skip") {
    const victim = neighbor(living, from, game.direction);
    if (victim !== game.judgeId) {
      game.skipped.push(victim);
      delete game.need[victim];
      game.table = game.table.filter((p) => p.from !== victim);
    }
    game.toast = `SKIP · ${nameOf(members, victim)} sits this one out. lol.`;
  } else if (uno === "wild") {
    game.prompt = { id: uid(), text: pick(PROMPTS) };
    game.toast = "WILD · new question. Keep going.";
  } else if (uno === "draw2") {
    game.need[from] = 2;
    const extra: PlayCard[] = [whiteCard(), whiteCard()];
    game.hands[from] = [...(game.hands[from] ?? []), ...extra];
    game.toast = "+2 · play two cards this round.";
  }
  if (Object.keys(game.need).length === 0 && game.phase === "play") {
    game.phase = "judge";
    game.table = shuffle(game.table);
  }
}

export function judgePick(game: GameState, members: Profile[], judgeId: string, playId: string): string | null {
  if (game.phase !== "judge") return "Nothing to judge but the juice.";
  if (judgeId !== game.judgeId) return "Only the tiny crown may choose.";
  const play = game.table.find((p) => p.id === playId);
  if (!play || play.card.kind !== "white") return "Point at an answer card.";
  const winner = members.find((m) => m.id === play.from);
  if (!winner) return "They already wandered off.";
  winner.score += 2;
  game.lastWinner = winner.id;
  game.winPlayId = play.id;
  game.lastClue = leakClue(game, members, winner.id);
  game.punchline = fillBlank(game.prompt?.text ?? "____", play.card.text);
  game.phase = "lol";
  game.narration = `${winner.name} got the lol. Sit with it.`;
  return null;
}

export function continueRound(game: GameState, members: Profile[]): void {
  if (game.phase !== "lol") return;
  if (game.round >= 5) {
    game.phase = "over";
    game.narration = "That’s family game night. Crown’s on the fridge. Peek at who’s who if you want.";
    return;
  }
  const winner = members.find((m) => m.id === game.lastWinner);
  if (winner) game.judgeId = winner.id;
  beginRound(game, members);
}

export function guessRole(game: GameState, members: Profile[], from: string, targetId: string, roleId: string): string {
  const secret = game.secrets[targetId];
  const guesser = members.find((m) => m.id === from);
  if (!secret || !guesser) return "The fridge shrugs.";
  if (secret.exposed) return "Already on the fridge.";
  if (from === targetId) return "You already know you. Sit down.";
  if (secret.roleId === roleId) {
    secret.exposed = true;
    guesser.score += 5;
    const role = roleById(roleId);
    return `Wait. ${nameOf(members, targetId)} is ${role?.title ?? "family"}. ${guesser.name} called it (+5).`;
  }
  guesser.score = Math.max(0, guesser.score - 1);
  return `Nope. ${guesser.name} loses one point and some dignity.`;
}

export function houseAct(game: GameState, members: Profile[]): boolean {
  if (game.phase === "play") {
    const bot = members.find((m) => m.bot && game.need[m.id]);
    if (!bot) return false;
    const hand = game.hands[bot.id] ?? [];
    const uno = hand.find((c) => c.kind === "uno");
    if (uno && Math.random() < 0.12) {
      playFromHand(game, members, bot.id, uno.id);
      return true;
    }
    const whites = hand.filter((c) => c.kind === "white");
    const pickCard = whites.sort((a, b) => b.text.length - a.text.length)[0] ?? hand[0];
    if (pickCard) playFromHand(game, members, bot.id, pickCard.id);
    return true;
  }
  if (game.phase === "judge") {
    const judge = members.find((m) => m.id === game.judgeId);
    if (!judge?.bot) return false;
    const whites = game.table.filter((p) => p.card.kind === "white");
    const play = whites[Math.floor(Math.random() * whites.length)] ?? whites[0];
    if (play) judgePick(game, members, judge.id, play.id);
    return true;
  }
  return false;
}

export function welcomeLate(game: GameState, id: string): void {
  if (game.phase === "lobby" || game.phase === "over") return;
  if (!game.secrets[id]) {
    const used = new Set(Object.values(game.secrets).map((s) => s.roleId));
    const role = ROLES.find((r) => !used.has(r.id)) ?? ROLES[0]!;
    game.secrets[id] = { roleId: role.id, clueIndex: 0, exposed: false, clues: [] };
  }
  if (!game.hands[id]) game.hands[id] = [];
  fillHands(game, [id]);
  if (game.phase === "play" && id !== game.judgeId && !game.skipped.includes(id)) {
    game.need[id] = game.need[id] ?? 1;
  }
}

function leakClue(game: GameState, members: Profile[], winnerId: string): string {
  const candidates = members.filter((m) => {
    const s = game.secrets[m.id];
    return s && !s.exposed && s.clueIndex < 4;
  });
  const target = candidates.find((m) => m.id !== winnerId) ?? candidates[0];
  if (!target) return "";
  const secret = game.secrets[target.id];
  if (!secret) return "";
  const role = roleById(secret.roleId);
  const clue = role?.clues[secret.clueIndex];
  if (!clue) return "";
  secret.clues.push(clue);
  secret.clueIndex += 1;
  if (secret.clueIndex >= 4) secret.exposed = true;
  return `${target.name}: ${clue}`;
}

function fillHands(game: GameState, ids: string[]): void {
  for (const id of ids) {
    const hand = game.hands[id] ?? [];
    let guard = 0;
    while (hand.filter((c) => c.kind === "white").length < 4 && guard++ < 50) {
      const next = whiteCard();
      if (hand.filter((c): c is WhiteCard => c.kind === "white").some((c) => c.text === next.text)) continue;
      hand.push(next);
    }
    if (!hand.some((c) => c.kind === "uno") && Math.random() < 0.35) hand.push(unoCard());
    game.hands[id] = hand;
  }
}

function rebuildNeed(game: GameState, living: Profile[]): void {
  game.need = {};
  for (const m of living) {
    if (m.id === game.judgeId || game.skipped.includes(m.id)) continue;
    const have = game.table.filter((p) => p.from === m.id && p.card.kind === "white").length;
    if (have < 1) game.need[m.id] = 1;
  }
}

function whiteCard(): WhiteCard {
  return { id: uid(), kind: "white", text: pick(WHITES) };
}

function unoCard(): PlayCard {
  const kinds: UnoKind[] = ["reverse", "skip", "wild", "draw2"];
  return { id: uid(), kind: "uno", uno: pick(kinds) };
}

function neighbor(members: Profile[], id: string, dir: 1 | -1): string {
  const i = members.findIndex((m) => m.id === id);
  if (i < 0) return members[0]?.id ?? id;
  const j = (i + dir + members.length) % members.length;
  return members[j]?.id ?? id;
}

function nameOf(members: Profile[], id: string): string {
  return members.find((m) => m.id === id)?.name ?? "someone";
}

function fillBlank(prompt: string, answer: string): string {
  return prompt.replace("____", answer);
}

function pick<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)] as T;
}

function shuffle<T>(list: T[]): T[] {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

export function publicGame(game: GameState, viewer: string): GameState {
  const hands: Record<string, PlayCard[]> = { [viewer]: game.hands[viewer] ?? [] };
  const secrets: Record<string, Secret> = {};
  for (const [id, secret] of Object.entries(game.secrets)) {
    secrets[id] = {
      ...secret,
      roleId: id === viewer || secret.exposed ? secret.roleId : "???",
    };
  }
  return { ...game, hands, secrets };
}
